import { Dispatch } from 'redux';
import KlaviyoController from '../controllers/KlaviyoController';
import { klaviyoErrorHandler } from '../errorHandlers/klaviyo-handler';
import {
  KlaviyoEventsResponse,
  KlaviyoListsResponse,
  KlaviyoRevenueResponse,
  OauthUrlResponse,
} from '../model/KlaviyoModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class KlaviyoCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveLists: string;
  revokeAction: string;
  retrieveAccounts: string;
  retrieveApiKeys: string;
  retrievedApiKey: string;
  resetStore: string;
  retrieveRevenue: string;
  retrieveEvents: string;

  constructor() {
    super('KLAVIYO');
    this.retrieveLists = this.makeActionKey('RETRIEVED_LISTS');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveApiKeys = this.makeActionKey('RETRIEVED_API_KEYS');
    this.retrievedApiKey = this.makeActionKey('RETRIEVED_API_KEY');
    this.revokeAction = this.makeActionKey('REVOKE');
    this.resetStore = this.makeActionKey('RESET_STORE');
    this.retrieveRevenue = this.makeActionKey('RETRIEVED_REVENUE');
    this.retrieveEvents = this.makeActionKey('RETRIEVED_EVENTS');
  }

  getOauthUrl(tokenId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse = await KlaviyoController.getOauthUrl(
          tokenId,
        );
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        klaviyoErrorHandler(dispatch)(error, this.retrieveOauthUrlAction);
      } finally {
        this.unbusy();
      }
    };
  }

  getApiKeys(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveApiKeys));
      try {
        const response = await KlaviyoController.getApiKeys(facebookPageId);
        dispatch({
          type: this.retrieveApiKeys,
          apiKeys: response,
        });
        return response;
      } catch (error) {
        return klaviyoErrorHandler(dispatch)(error, this.retrieveApiKeys);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  createApiKey(privateKey: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const res = await KlaviyoController.createApiKey(privateKey);

        dispatch({ type: this.retrievedApiKey, apiKey: res });
        return res;
      } catch (error) {
        return klaviyoErrorHandler(dispatch)(error, this.retrievedApiKey);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  updateApiKey(id: number, privateKey: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const res = await KlaviyoController.updateApiKey(id, privateKey);

        dispatch({ type: this.retrievedApiKey, apiKey: res });
        return res;
      } catch (error) {
        return klaviyoErrorHandler(dispatch)(error, this.retrievedApiKey);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await KlaviyoController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        return klaviyoErrorHandler(dispatch)(error, this.retrieveAccounts);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revoke(tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await KlaviyoController.revokeToken(tokenId);
        dispatch({ type: this.revokeAction, id: tokenId });
        return true;
      } catch (error) {
        klaviyoErrorHandler(dispatch)(error, this.revokeAction);
        return false;
      }
    };
  }

  getRevenues(
    facebookPageId: number,
    force: boolean = false,
    skipBusy?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveRevenue));
      }

      try {
        const response: KlaviyoRevenueResponse =
          await KlaviyoController.getRevenue(facebookPageId, force);
        dispatch({
          type: this.retrieveRevenue,
          revenue: response,
        });
        return response;
      } catch (error) {
        klaviyoErrorHandler(dispatch)(error, this.retrieveRevenue);
      }
    };
  }

  getEvents(facebookPageId: number, force: boolean = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveEvents));
      try {
        const response: KlaviyoEventsResponse =
          await KlaviyoController.getEvents(facebookPageId, force);
        dispatch({
          type: this.retrieveEvents,
          events: response.eventsByMetric,
        });
        return response;
      } catch (error) {
        klaviyoErrorHandler(dispatch)(error, this.retrieveEvents);
      }
    };
  }

  getLists(
    facebookPageId: number,
    tokenId: number,
    tokenType: 'api_key' | 'oauth_token',
    force: boolean = false,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveLists));
      try {
        const response: KlaviyoListsResponse = await KlaviyoController.getLists(
          facebookPageId,
          tokenId,
          tokenType,
          force,
        );
        dispatch({
          type: this.retrieveLists,
          lists: response,
        });
        return response;
      } catch (error) {
        klaviyoErrorHandler(dispatch)(error, this.retrieveLists);
      }
    };
  }

  invalidateStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const klaviyoCRUDActions = new KlaviyoCRUDActions();

export default klaviyoCRUDActions;

export const useKlaviyoCRUDActions = getActionHookBuilder(klaviyoCRUDActions, [
  'getLists',
  'revoke',
  'getAccounts',
  'getRevenues',
  'getEvents',
  'getApiKeys',
  'getOauthUrl',
  'createApiKey',
  'updateApiKey',
  'invalidateStore',
]);
